import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { knowledgeAPI } from '../services/api';
import colors, { shadows, borderRadius } from '../theme';

function LibraryPage() {
    const [knowledge, setKnowledge] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [user, setUser] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        // Get user from localStorage
        const userData = localStorage.getItem('user');
        if (userData) {
            setUser(JSON.parse(userData));
        }

        loadKnowledge();
    }, []);

    const loadKnowledge = async () => {
        try {
            const response = await knowledgeAPI.getAll();
            setKnowledge(response.data.data);
            setLoading(false);
        } catch (error) {
            console.error('Error loading knowledge:', error);
            setLoading(false);
        }
    };

    const [togglingId, setTogglingId] = useState(null);

    const handleToggleQuiz = async (e, itemId) => {
        e.stopPropagation(); // Prevent navigation when clicking toggle

        setTogglingId(itemId);

        try {
            const response = await knowledgeAPI.toggleQuiz(itemId);

            // Update local state
            setKnowledge(knowledge.map(item =>
                item.id === itemId
                    ? { ...item, quiz_enabled: response.data.quiz_enabled }
                    : item
            ));
        } catch (error) {
            console.error('Error toggling quiz:', error);
            alert('Failed to toggle quiz status');
        } finally {
            setTogglingId(null);
        }
    };

    const filteredKnowledge = knowledge.filter(item =>
        item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.description?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (loading) {
        return <div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>;
    }

    return (
        <div style={{
            minHeight: '100vh',
            background: `linear-gradient(135deg, ${colors.gray[50]} 0%, ${colors.blue[50]} 100%)`
        }}>
            {/* Header */}


            {/* Content */}
            <div style={{ padding: '2rem', maxWidth: '1200px', margin: '0 auto' }}>
                {/* Search */}
                <input
                    type="text"
                    placeholder="Search knowledge..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: `1px solid ${colors.gray[300]}`,
                        borderRadius: '8px',
                        fontSize: '1rem',
                        marginBottom: '1.5rem'
                    }}
                />

                {/* Column Headers */}
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: user?.role === 'admin'
                        ? '2fr 1fr 1fr 0.75fr 1fr 1fr'
                        : '2fr 1fr 1fr 0.75fr 1fr',
                    gap: '1rem',
                    padding: '0.5rem 1rem',
                    marginBottom: '0.5rem',
                    borderBottom: `2px solid ${colors.gray[200]}`
                }}>
                    <div style={{ fontSize: '0.75rem', fontWeight: '600', color: colors.gray[700], textTransform: 'uppercase' }}>
                        Title
                    </div>
                    <span style={{ fontSize: '0.75rem', fontWeight: '600', color: colors.gray[700], textTransform: 'uppercase' }}>
                        Created
                    </span>
                    <span style={{ fontSize: '0.75rem', fontWeight: '600', color: colors.gray[700], textTransform: 'uppercase' }}>
                        Last Attempt
                    </span>
                    <span style={{ fontSize: '0.75rem', fontWeight: '600', color: colors.gray[700], textTransform: 'uppercase', textAlign: 'center' }}>
                        Score
                    </span>
                    {user?.role === 'admin' && (
                        <span style={{ fontSize: '0.75rem', fontWeight: '600', color: colors.gray[700], textTransform: 'uppercase' }}>
                            Actions
                        </span>
                    )}
                    <span style={{ fontSize: '0.75rem', fontWeight: '600', color: colors.gray[700], textTransform: 'uppercase' }}>
                        Status
                    </span>
                </div>

                {/* Knowledge List */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    {filteredKnowledge.map((item) => {
                        const isDisabled = item.quiz_enabled === false;
                        const isAdmin = user?.role === 'admin';

                        // Calculate status
                        let statusColor = colors.green[500];
                        let statusBg = colors.green[50];
                        let statusText = '✓ Available';

                        if (isDisabled && isAdmin) {
                            statusColor = colors.gray[600];
                            statusBg = colors.gray[100];
                            statusText = '🚫 Disabled';
                        } else if (item.user_score !== null) {
                            if (item.user_score === 100) {
                                // Completed
                                statusColor = colors.blue[700];
                                statusBg = colors.blue[100];
                                statusText = '✅ Complete';
                            } else {
                                // Failed - check if locked (within 48 hours)
                                const completedDate = new Date(item.user_completed_at);
                                const now = new Date();
                                const hoursSince = (now - completedDate) / (1000 * 60 * 60);

                                if (hoursSince < 48) {
                                    // Practice Mode
                                    statusColor = colors.orange[700];
                                    statusBg = colors.orange[100];
                                    const hoursLeft = Math.ceil(48 - hoursSince);
                                    statusText = `🔥 Practice (${hoursLeft}h)`;
                                } else {
                                    // Available to retry
                                    statusColor = colors.green[700];
                                    statusBg = colors.green[50];
                                    statusText = '✓ Available';
                                }
                            }
                        }

                        // Calculate score fraction
                        let scoreFraction = null;
                        if (item.user_answers && item.quiz_questions) {
                            const userAnswers = typeof item.user_answers === 'string'
                                ? JSON.parse(item.user_answers)
                                : item.user_answers;
                            const quizQuestions = typeof item.quiz_questions === 'string'
                                ? JSON.parse(item.quiz_questions)
                                : item.quiz_questions;

                            let correctCount = 0;
                            userAnswers.forEach((answer, idx) => {
                                if (quizQuestions[idx] && answer === quizQuestions[idx].correctAnswer) {
                                    correctCount++;
                                }
                            });
                            scoreFraction = `${correctCount}/${quizQuestions.length}`;
                        }

                        // Format dates
                        const createdDate = new Date(item.created_at).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                        });
                        const lastAttemptDate = item.user_completed_at
                            ? new Date(item.user_completed_at).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric'
                              })
                            : null;

                        return (
                            <div
                                key={item.id}
                                style={{
                                    backgroundColor: colors.white,
                                    padding: '0.75rem 1rem',
                                    borderRadius: borderRadius.md,
                                    boxShadow: shadows.sm,
                                    cursor: (isDisabled && !isAdmin) ? 'not-allowed' : 'pointer',
                                    opacity: (isDisabled && isAdmin) ? 0.5 : 1,
                                    border: (isDisabled && isAdmin) ? `2px dashed ${colors.gray[300]}` : 'none',
                                    display: 'grid',
                                    gridTemplateColumns: user?.role === 'admin'
                                        ? '2fr 1fr 1fr 0.75fr 1fr 1fr'
                                        : '2fr 1fr 1fr 0.75fr 1fr',
                                    gap: '1rem',
                                    alignItems: 'center'
                                }}
                                onClick={() => {
                                    if (!(isDisabled && !isAdmin)) {
                                        navigate(`/knowledge/${item.id}`);
                                    }
                                }}
                            >
                                {/* Title */}
                                <div style={{ minWidth: 0 }}>
                                    <h3 style={{
                                        margin: 0,
                                        fontSize: '0.95rem',
                                        fontWeight: '600',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap'
                                    }}>
                                        {item.title}
                                    </h3>
                                </div>

                                {/* Created Date */}
                                <span style={{
                                    fontSize: '0.7rem',
                                    color: colors.gray[600],
                                    whiteSpace: 'nowrap'
                                }}>
                                    {createdDate}
                                </span>

                                {/* Last Attempt */}
                                <span style={{
                                    fontSize: '0.7rem',
                                    color: colors.gray[600],
                                    whiteSpace: 'nowrap'
                                }}>
                                    {lastAttemptDate || '—'}
                                </span>

                                {/* Score Fraction */}
                                <span style={{
                                    fontSize: '0.7rem',
                                    color: colors.gray[600],
                                    fontWeight: '600',
                                    whiteSpace: 'nowrap',
                                    textAlign: 'center'
                                }}>
                                    {scoreFraction || '—'}
                                </span>

                                {/* Admin toggle button */}
                                {isAdmin && (
                                    <button
                                        onClick={(e) => handleToggleQuiz(e, item.id)}
                                        disabled={togglingId === item.id}
                                        style={{
                                            fontSize: '0.65rem',
                                            padding: '0.3rem 0.7rem',
                                            backgroundColor: togglingId === item.id
                                                ? colors.gray[400]
                                                : isDisabled ? colors.green[600] : colors.red[600],
                                            color: colors.white,
                                            border: 'none',
                                            borderRadius: borderRadius.sm,
                                            cursor: togglingId === item.id ? 'wait' : 'pointer',
                                            fontWeight: '600',
                                            transition: 'all 0.2s ease',
                                            boxShadow: togglingId === item.id ? 'none' : shadows.sm,
                                            opacity: togglingId === item.id ? 0.7 : 1,
                                            whiteSpace: 'nowrap'
                                        }}
                                    >
                                        {togglingId === item.id
                                            ? 'Updating...'
                                            : isDisabled ? '✓ Enable' : '🔒 Disable'}
                                    </button>
                                )}

                                {/* Status Badge */}
                                <span style={{
                                    fontSize: '0.7rem',
                                    padding: '0.2rem 0.6rem',
                                    backgroundColor: statusBg,
                                    color: statusColor,
                                    borderRadius: '4px',
                                    fontWeight: '600',
                                    whiteSpace: 'nowrap'
                                }}>
                                    {statusText}
                                </span>
                            </div>
                        );
                    })}

                </div>
            </div>
        </div>
    );
}

export default LibraryPage;